<template>
  <div v-if="page_loading">
    <loading :page_loading="page_loading" />
  </div>
  <div v-else>
    <loading :loading="loading" />

    <!-- นำทาง -->
    <v-card class="v-card-bottom-30">
      <v-card-title>
        {{ use_language.oms }}
        <v-icon>mdi-chevron-right</v-icon>
        <span v-if="isEdit">{{ use_language.edit_order_request }} {{ editing.requestNo }}</span>
        <span v-else>{{ use_language.add_order }}</span>
      </v-card-title>
    </v-card>

    <div v-if="locations.length == 0">
      <v-card elevation="1" class="v-card-bottom-30">
        <v-card-text class="text-center py-6" style="color: #9e9e9e;">{{ use_language.no_location_assigned }}</v-card-text>
      </v-card>
    </div>

    <div v-else>
      <!-- คำขอที่ถูกตีกลับ: แสดงเหตุผลไว้ด้านบน -->
      <v-alert v-if="isEdit && editing.lastRejectReason" type="error" dense text class="v-card-bottom-30">
        <b>{{ use_language.reject_reason }} :</b> {{ editing.lastRejectReason }}
      </v-alert>

      <!-- ร้านค้า -->
      <v-card elevation="1" class="v-card-bottom-30">
        <v-card-title class="height-57">
          <v-icon size="22" color="success darken-3">mdi-storefront</v-icon><span class="ml-3">{{ use_language.store }}</span>
        </v-card-title>
        <v-divider/>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="2" class="pa-1 pt-3">{{ use_language.location }} :</v-col>
            <v-col cols="12" md="6" class="pa-1">
              <!-- ผู้ใช้ที่สังกัด location เดียวถูกล็อกไว้ และคำขอที่แก้ไขเปลี่ยน location ไม่ได้ -->
              <v-autocomplete
                :value="locationId"
                :items="locations"
                :item-text="item => item.channel + ' : ' + item.name"
                item-value="id"
                :disabled="locations.length == 1 || isEdit"
                outlined
                dense
                hide-details
                @change="onLocationChange"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="2" class="pa-1 pt-3">{{ use_language.store }} :</v-col>
            <v-col cols="12" md="6" class="pa-1">
              <v-autocomplete
                v-model="store"
                :items="storeItems"
                :item-text="storeText"
                item-value="shopCustomerID"
                :search-input.sync="storeKeyword"
                :loading="storeLoading"
                :placeholder="use_language.search + ' ' + use_language.store_name + ' / ' + use_language.store_code"
                :no-filter="storeServerSearch"
                :filter="storeFilter"
                return-object
                clearable
                outlined
                dense
                hide-details
              ></v-autocomplete>
            </v-col>
          </v-row>
          <!-- ที่อยู่และ profile ของร้านขึ้นอัตโนมัติจาก master ร้านค้า -->
          <v-row v-if="store != null">
            <v-col cols="12" md="2" class="pa-1"></v-col>
            <v-col cols="12" md="10" class="pa-1">
              <div>{{ use_language.contact }} : {{ store.contactName }} {{ store.phone }}</div>
              <div>{{ use_language.address }} : {{ fullAddress(store) }}</div>
              <div v-if="store.shippingAddressID == null" style="color: red;">{{ use_language.store_has_no_address }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- สินค้า -->
      <v-card elevation="1" class="v-card-bottom-30">
        <v-card-title class="height-57">
          <v-icon size="22" color="success darken-3">mdi-package-variant-closed</v-icon><span class="ml-3">{{ use_language.product }}</span>
          <v-spacer/>
          <span class="fn-14">{{ use_language.selected }} {{ formatMoney(String(selectedCount)) }} {{ use_language.items }} / {{ formatMoney(String(totalQuantity)) }} {{ use_language.pieces }}</span>
        </v-card-title>
        <v-divider/>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="5" class="pa-1">
              <v-text-field
                v-model="productKeyword"
                :placeholder="use_language.search + ' ' + use_language.product + ' / ' + use_language.sku"
                prepend-inner-icon="mdi-magnify"
                autocomplete="off"
                outlined
                dense
                hide-details
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="7" class="pa-1 d-flex align-center">
              <v-checkbox v-model="onlySelected" :label="use_language.only_selected" hide-details class="mt-0"></v-checkbox>
            </v-col>
          </v-row>

          <v-data-table
            :headers="productHeader"
            :items="filteredProducts"
            :items-per-page="50"
            :footer-props="{ 'items-per-page-options': [50, 100, -1] }"
            :no-data-text="use_language.no_product_in_location"
            class="packhai-border-table mt-3"
          >
            <template v-slot:item.photoLink="{ item }">
              <v-img v-if="item.photoLink" :src="item.photoLink" width="44" height="44" contain class="my-1 mx-auto"></v-img>
            </template>
            <template v-slot:item.productName="{ item }">
              <div>{{ item.productName }}</div>
              <div style="font-size: 12px; color: #9e9e9e;">{{ item.sku }}<span v-if="item.warehouseName"> · {{ item.warehouseName }}</span></div>
              <div v-if="problems[item.stockShopID]" style="font-size: 12px; color: red;">{{ problems[item.stockShopID] }}</div>
            </template>
            <template v-slot:item.availableQty="{ item }">
              <b :style="item.availableQty > 0 ? 'color: darkgreen;' : 'color: red;'">{{ formatMoney(String(item.availableQty)) }}</b>
            </template>
            <template v-slot:item.quantity="{ item }">
              <v-text-field
                :value="quantities[item.stockShopID] || ''"
                type="number"
                min="0"
                :max="item.availableQty"
                :disabled="item.availableQty <= 0 && !quantities[item.stockShopID]"
                outlined
                dense
                hide-details
                class="my-1"
                :background-color="quantities[item.stockShopID] ? '#FFF8E1' : ''"
                :error="isOver(item) || problems[item.stockShopID] != null"
                @input="value => setQuantity(item, value)"
              ></v-text-field>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- หมายเหตุ และส่ง -->
      <v-card elevation="1" class="v-card-bottom-30">
        <v-card-text>
          <v-row>
            <v-col cols="12" md="2" class="pa-1 pt-3">{{ use_language.remark }} :</v-col>
            <v-col cols="12" md="10" class="pa-1">
              <v-textarea v-model="remark" rows="2" outlined dense hide-details></v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pa-1 pt-3" align="right">
              <v-btn v-if="isEdit" :elevation="1" outlined class="px-2 mr-2" @click="$router.push('/oms/order-detail?id=' + editing.tempID)">{{ use_language.cancel }}</v-btn>
              <v-btn :elevation="1" color="primary" class="px-2" @click="Submit()">
                <v-icon size="20" class="mr-1">mdi-send</v-icon>
                <span class="fn-14"><b>{{ isEdit ? use_language.resubmit : (isDirect ? use_language.create_order_now : use_language.submit_for_approval) }}</b></span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
  import Loading from '@/website/components/Loading'
  import { formatMoney, format_order_id } from '@/website/global_function'
  import { getOmsLanguage } from '@/website/backend/oms/oms_language'
  import {
    getOmsContext, getStoreList, getStockList, getOrderDefaults,
    createOrderRequest, updateOrderRequest, getOrderRequestDetail
  } from '@/website/backend/oms/oms_service'

  const OMS_REFERENCE_SOURCE_ID = 1
  // จำนวนร้านสูงสุดที่โหลดมาทั้งหมดในครั้งแรก (เท่ากับเพดานต่อคำขอของ backend)
  const STORE_LOAD_LIMIT = 500

  function newToken () {
    return 'oms-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 12) + Math.random().toString(36).substring(2, 12)
  }

  export default {
    components: {
      Loading
    },
    data: () => ({
      // page
      page_loading: true,
      loading: false,
      use_language: getOmsLanguage(),

      // คำขอที่กำลังแก้ไข (null = สร้างใหม่)
      editing: null,
      createToken: newToken(),
      defaults: null,

      locations: [],  // เฉพาะช่องทาง ON ที่ผู้ใช้มีสิทธิ์
      locationId: null,

      store: null,
      storeItems: [],
      storeKeyword: null,
      storeServerSearch: false,  // true = location นี้มีร้านเกินเพดาน ต้องค้นหาที่ backend
      storeLoading: false,
      storeTimer: null,

      products: [],
      productKeyword: null,
      onlySelected: false,
      quantities: {},  // stockShopID -> จำนวนที่สั่ง
      problems: {},    // stockShopID -> ข้อความที่ backend ไม่ให้ผ่าน
      remark: null
    }),
    computed: {
      isEdit () {
        return this.editing != null
      },
      // ช่องทาง TT ไม่มีการอนุมัติ: กดแล้วสร้างออเดอร์จริงทันที
      isDirect () {
        const location = this.locations.find(item => item.id == this.locationId)
        return location != null && location.channel == 'TT'
      },
      selectedCount () {
        return Object.keys(this.quantities).length
      },
      totalQuantity () {
        return Object.keys(this.quantities).reduce((sum, key) => sum + this.quantities[key], 0)
      },
      filteredProducts () {
        const keyword = (this.productKeyword || '').trim().toLowerCase()
        return this.products.filter(item =>
          (!this.onlySelected || this.quantities[item.stockShopID]) &&
          (keyword == '' || (item.productName || '').toLowerCase().includes(keyword) || (item.sku || '').toLowerCase().includes(keyword))
        )
      },
      productHeader () {
        return [
          { text: '', align: 'center', value: 'photoLink', sortable: false, width: '70px' },
          { text: this.use_language.product, align: 'left', value: 'productName', sortable: false },
          { text: this.use_language.available_qty, align: 'right', value: 'availableQty', sortable: false, width: '130px' },
          { text: this.use_language.order_qty, align: 'center', value: 'quantity', sortable: false, width: '160px' }
        ]
      }
    },
    watch: {
      // location ที่มีร้านเกินเพดาน: ค้นหาที่ backend ตามที่พิมพ์ ส่วน location ปกติโหลดครบแล้วและกรองในหน้าจอ
      storeKeyword (keyword) {
        if (!this.storeServerSearch) {
          return
        }
        // ข้อความในช่องเท่ากับร้านที่เลือกอยู่ = เพิ่งเลือกร้าน ไม่ใช่การพิมพ์ค้นหา
        if (this.store != null && keyword == this.storeText(this.store)) {
          return
        }
        clearTimeout(this.storeTimer)
        this.storeTimer = setTimeout(() => this.loadStores(keyword), 300)
      }
    },
    async created () {
      document.title = this.$router.history.current.meta.title
      const context = await getOmsContext()
      // Sale เห็นเฉพาะ location ของช่องทางตัวเอง ส่วน Admin สร้างได้ทุก location
      // location ของ ON ต้องรออนุมัติ ส่วน TT สร้างออเดอร์ทันที
      this.locations = context.locations || []

      const defaults = await getOrderDefaults()
      this.defaults = defaults.defaults

      const tempId = Number(this.$route.query.tempId)
      if (tempId > 0) {
        await this.loadEditing(tempId)
      } else if (this.locations.length > 0) {
        this.locationId = this.locations[0].id
        await this.loadLocationData()
      }
      this.page_loading = false
    },
    methods: {
      formatMoney,
      showError (response) {
        this.$swal({
          position: 'top',
          type: 'error',
          text: response.message || (response.errorCode == 'NETWORK' ? this.use_language.network_error : this.use_language.cannot_perform_operation)
        })
      },
      // ชื่อร้านตามด้วยรหัสร้านเมื่อมี (ร้านที่เพิ่มเองอาจไม่มีรหัส)
      storeText (item) {
        return item.storeName + (item.storeCode ? ' (' + item.storeCode + ')' : '')
      },
      fullAddress (item) {
        return [item.address, item.subDistrict, item.district, item.province, item.postcode].filter(text => !!text).join(' ')
      },
      async loadEditing (tempId) {
        const response = await getOrderRequestDetail(tempId)
        if (response.status != 'success' || !response.canEdit) {
          // แก้ไขได้เฉพาะคำขอที่ถูกตีกลับและเป็นของตัวเอง
          this.$router.replace('/oms/order')
          return
        }
        this.editing = response.request
        this.locationId = response.request.locationID
        this.remark = response.remark
        await this.loadLocationData()
        // ร้านของคำขอเดิม: เลือกไว้ และเพิ่มเข้ารายการถ้ายังไม่อยู่ (เช่น ร้านถูกย้ายไป location อื่นแล้ว)
        this.store = response.store
        if (response.store != null && !this.storeItems.some(item => item.shopCustomerID == response.store.shopCustomerID)) {
          this.storeItems.unshift(response.store)
        }
        const quantities = {}
        for (const item of response.items) {
          quantities[item.stockShopID] = item.quantity
        }
        this.quantities = quantities
      },
      async onLocationChange (locationId) {
        // ร้านค้าและสินค้าผูกกับ location จึงล้างที่เลือกไว้เมื่อเปลี่ยน
        this.locationId = locationId
        this.store = null
        this.quantities = {}
        this.problems = {}
        await this.loadLocationData()
      },
      async loadLocationData () {
        this.loading = true
        await Promise.all([this.loadStores(null), this.loadProducts()])
        this.loading = false
      },
      async loadStores (keyword) {
        if (this.locationId == null) {
          return
        }
        // ครั้งแรกของ location (keyword = null) โหลดร้านทั้งหมดไม่เกินเพดาน ถ้าได้ครบจะกรองในหน้าจอ ไม่ต้องเรียก backend อีก
        // ถ้า location มีร้านเกินเพดาน จะสลับเป็นค้นหาที่ backend ตามที่พิมพ์
        const isInitial = keyword == null || keyword === ''
        this.storeLoading = true
        const response = await getStoreList({
          locationId: this.locationId,
          keyword: isInitial ? null : keyword,
          skip: 0,
          take: isInitial ? STORE_LOAD_LIMIT : 50
        })
        this.storeLoading = false
        if (response.status == 'success') {
          if (isInitial) {
            this.storeServerSearch = response.searchResultCount > response.items.length
          }
          // คงร้านที่เลือกอยู่ไว้ในรายการเสมอ ไม่อย่างนั้นตัวเลือกจะว่างเมื่อผลค้นหาไม่มีร้านนั้น
          const items = response.items
          if (this.store != null && !items.some(item => item.shopCustomerID == this.store.shopCustomerID)) {
            items.unshift(this.store)
          }
          this.storeItems = items
        }
      },
      // กรองในหน้าจอจากชื่อร้าน รหัสร้าน และเบอร์โทร (ใช้เมื่อโหลดร้านของ location มาครบแล้ว)
      storeFilter (item, queryText) {
        const keyword = (queryText || '').trim().toLowerCase()
        return (item.storeName || '').toLowerCase().includes(keyword) ||
          (item.storeCode || '').toLowerCase().includes(keyword) ||
          (item.phone || '').includes(keyword)
      },
      async loadProducts () {
        if (this.locationId == null) {
          return
        }
        const response = await getStockList({ locationId: this.locationId, skip: 0, take: 500 })
        if (response.status != 'success') {
          this.showError(response)
          return
        }
        this.products = response.items
      },
      setQuantity (item, value) {
        this.$delete(this.problems, item.stockShopID)
        const number = value === '' || value == null ? 0 : Math.floor(Number(value))
        if (isNaN(number) || number <= 0) {
          this.$delete(this.quantities, item.stockShopID)
        } else {
          this.$set(this.quantities, item.stockShopID, number)
        }
      },
      isOver (item) {
        return (this.quantities[item.stockShopID] || 0) > item.availableQty
      },
      async Submit () {
        if (this.store == null) {
          this.$swal({ position: 'top', type: 'warning', text: this.use_language.please_select_store })
          return
        }
        if (this.store.shippingAddressID == null) {
          this.$swal({ position: 'top', type: 'warning', text: this.use_language.store_has_no_address })
          return
        }
        if (this.selectedCount == 0) {
          this.$swal({ position: 'top', type: 'warning', text: this.use_language.please_select_product })
          return
        }
        const selected = this.products.filter(item => this.quantities[item.stockShopID])
        if (selected.some(item => this.isOver(item))) {
          this.$swal({ position: 'top', type: 'warning', text: this.use_language.order_qty_over_available })
          return
        }
        // สินค้าที่เลือกไว้แต่ location ไม่มีโควตาแล้ว (เช่น คำขอเก่าที่ถูกตีกลับ) จะไม่อยู่ในรายการสินค้า
        if (selected.length != this.selectedCount) {
          this.$swal({ position: 'top', type: 'warning', text: this.use_language.product_no_longer_available })
          this.onlySelected = false
          return
        }

        const body = {
          tempId: this.isEdit ? this.editing.tempID : 0,
          omsLocationId: this.locationId,
          // payload ชุดเดียวกับ Order/new-order: backend เก็บทั้งก้อน และใช้สร้างออเดอร์จริงเมื่อ Admin อนุมัติ
          // ตัวตน คลัง และรูปแบบออเดอร์ถูก backend กำหนดทับอีกชั้น
          newOrderRequest: {
            orderMaster: {
              shopId: Number(localStorage.getItem('shop_id')),
              branchId: Number(localStorage.getItem('branch_id')) || 0,
              createdByStaffId: Number(localStorage.getItem('staff_id')),
              salemanId: Number(localStorage.getItem('staff_id')),
              shippingStatusId: 0,
              totalPrice: 0,
              deliveryCost: 0,
              vatType: 1,
              isCOD: false,
              shopAddressId: this.defaults.shopAddressId,
              // คำสั่งซื้อของ OMS ไม่ระบุขนส่ง และใช้ช่องทางการขาย 1 เสมอ
              expressCompanyId: null,
              shopExpressID: null,
              branchExpressID: null,
              referenceSourceId: OMS_REFERENCE_SOURCE_ID,
              createToken: this.createToken,
              remark: this.remark
            },
            orderItem: selected.map(item => ({
              stockShopId: item.stockShopID,
              quantity: this.quantities[item.stockShopID],
              unitPrice: 0,
              discount: null,
              discountPercent: null,
              isLock: true,
              productMasterID: item.productMasterID,
              productMasterItemID: item.productMasterItemID,
              isProductSet: false
            })),
            b2bInsert: {
              customerType: 2,
              shopCustomerID: this.store.shopCustomerID,
              shippingAddressID: this.store.shippingAddressID,
              isQuotation: false,
              isReserveStock: false
            }
          }
        }

        this.loading = true
        const response = this.isEdit ? await updateOrderRequest(body) : await createOrderRequest(body)
        this.loading = false
        if (response.status != 'success') {
          if (response.errorCode == 'QUOTA_EXCEEDED') {
            // โควตาถูกคนอื่นใช้ไประหว่างกรอก: แสดงคงเหลือล่าสุดที่แถวของสินค้านั้น
            const problems = {}
            for (const item of response.items || []) {
              problems[item.stockShopID] = this.use_language.available_now + ' ' + item.availableQty
            }
            this.problems = problems
            await this.loadProducts()
          }
          this.showError(response)
          return
        }
        await this.$swal({
          position: 'top',
          type: 'success',
          title: response.orderMasterID ? format_order_id(response.orderMasterID) : response.requestNo,
          text: response.orderMasterID ? this.use_language.order_created_direct : this.use_language.submitted_wait_manager
        })
        this.$router.push('/oms/order-detail?id=' + response.tempID)
      }
    }
  }
</script>
